import React from "react";
import { Observer, useLocalStore } from "mobx-react";
import { Paper } from "@material-ui/core";
import locale from "@bonliva-crm/locale";
import { PrimaryButton, SpinnerOverlay, TabBadge } from "@components";
import { ConsultantBookingContractType } from "@graphql";
import { Form, FormxDebugger } from "@common/formx";
import { ContractFormStore } from "./ContractFormStore";
import { initialValues } from "./utils";
import * as f from "./fields";
import { DynamicFieldsGroup } from "@consultants/ConsultantContract/DynamicFieldsGroup";
import { TabFormLayout } from "@common/forms/TabFormLayout";
import { useNavigation } from "@navigation";
import { ContractUploaderDialog } from "@consultants/ConsultantContract/ContractUploaderDialog";
import { useTenant } from "../../tenant/tenant";

type Props = {
  contractId?: string;
  action?: string;
  consultantId: string;
};

export const ContractBookingForm = ({ consultantId, contractId, action }: Props) => {
  const navigator = useNavigation();
  const { tenant } = useTenant();
  const localStore = useLocalStore(
    () =>
      new ContractFormStore(
        initialValues,
        consultantId,
        () => handleClose(),
        tenant,
        contractId,
        action
      )
  );

  const handleClose = (): void => {
    localStore.form.reset({ ...initialValues, ConsultantId: consultantId });
    localStore.fields = [f.contractType];
    setTimeout(() => {
      navigator.close();
    }, 1000);
  };

  if (!localStore) {
    return null;
  }

  return (
    <Observer>
      {() => {
        const { form, loading, fields, isEdit, hasErrorsInMainTab } = localStore;

        const renderMain = (
          <Paper
            elevation={0}
            style={{ height: "100%", padding: "0 24px", position: "relative" }}
          >
            <DynamicFieldsGroup
              form={form}
              fields={fields}
              extraProps={{ store: localStore }}
            />
          </Paper>
        );

        const price = fields
          ?.filter((x) => x.name === "Salary" || x.name === "Bonus")
          .map((x) => ({
            ...x,
            show: () => true
          }));

        const renderPrice = (
          <Paper elevation={0} style={{ height: "100%", position: "relative" }}>
            <DynamicFieldsGroup
              skipChangeSchema
              form={form}
              fields={price}
              extraProps={{ store: localStore }}
            />
          </Paper>
        );

        const priceRecruitment = fields
          ?.filter((x) => x.name === "PriceRecruitment")
          .map((x) => ({
            ...x,
            show: () => true
          }));

        const renderPriceRecruitment = (
          <Paper elevation={0} style={{ height: "100%", position: "relative" }}>
            <DynamicFieldsGroup
              skipChangeSchema
              form={form}
              fields={priceRecruitment}
              extraProps={{ store: localStore }}
            />
          </Paper>
        );

        const tabs = [
          {
            label: (
              <TabBadge
                label={isEdit ? locale.Edit : `${locale.New} ${locale.Contract}`}
                hasErrors={hasErrorsInMainTab}
              />
            ),
            value: "main",
            content: renderMain
          },
          {
            label: (
              <TabBadge
                label={locale.SalaryCompensationBookingContract}
                hasErrors={
                  !!form.errors.BasicPrice ||
                  !!form.errors.SpecificPeriodPrices ||
                  localStore.hasErrorsInSalaryOption
                }
              />
            ),
            value: "price",
            content: renderPrice
          },
          {
            label: (
              <TabBadge
                label={locale.recruitmentFees}
                hasErrors={!!form.errors.PriceRecruitment}
              />
            ),
            value: "priceRecruitment",
            content: renderPriceRecruitment
          }
        ].filter((x) => {
          return (
            (price?.length ? true : x.value !== "price") &&
            (priceRecruitment?.length ? true : x.value !== "priceRecruitment")
          );
        });

        const handleOpenTicket = (): void => {
          localStore.isTicket = true;
          localStore.form.submit();
        };

        const isSigned = !!localStore.initialData?.SignedOn;

        const extraBtns = (
          <Observer>
            {() => (
              <>
                {isEdit && (
                  <PrimaryButton
                    onClick={() => localStore.handleTicket(localStore.ticketData)}
                    disabled={form.dirty || form.isSubmitting}
                  >
                    {locale.AddTicket}
                  </PrimaryButton>
                )}

                {!isSigned &&
                  localStore.form.values.ContractType !==
                    ConsultantBookingContractType.Confirmation && (
                    <PrimaryButton
                      onClick={() => localStore.handleSaveAndSendToOneflow()}
                      disabled={!localStore.canSubmit || form.isSubmitting}
                      loading={localStore.isPublishingToOneFlow && form.isSubmitting}
                    >
                      Skapa och skicka till OneFlow
                    </PrimaryButton>
                  )}

                {!isSigned &&
                  localStore.form.values.ContractType !==
                    ConsultantBookingContractType.Confirmation && (
                    <PrimaryButton
                      onClick={handleOpenTicket}
                      disabled={!localStore.canSubmit || form.isSubmitting}
                      loading={localStore.isTicket && form.isSubmitting}
                    >
                      {locale.SaveAndCreateTicket}
                    </PrimaryButton>
                  )}
              </>
            )}
          </Observer>
        );

        return (
          <div>
            <SpinnerOverlay show={loading} />
            <Form store={form} allowUpdates>
              <TabFormLayout
                blockId="consultant_booking_contract_form"
                canSubmit={localStore.canSubmit && !form.isSubmitting}
                isDirty={form.dirty}
                isSubmitting={
                  form.isSubmitting &&
                  !localStore.isTicket &&
                  !localStore.isPublishingToOneFlow
                }
                onSubmit={form.onSubmit}
                onReset={form.onResetHandler}
                extraButtons={extraBtns}
                onClose={localStore.handleClose}
                tabs={tabs}
              >
                <FormxDebugger store={localStore.form} />
              </TabFormLayout>

              <FormxDebugger store={form} />

              <ContractUploaderDialog open={localStore.isPublishingToOneFlow} />
            </Form>
          </div>
        );
      }}
    </Observer>
  );
};
